import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Tabs,
  Tab,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  TextField,
  Divider,
  LinearProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const FREQ_OPTIONS = [
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'half_yearly', label: 'Half-yearly' },
  { key: 'yearly', label: 'Yearly' }
];

function employeeLabel(row) {
  const c = row?.employee?.Contact || row?.employee?.contact;
  const name = c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '';
  const code = row?.employee?.employee_code || '';
  return name || code || row?.employee_id || '—';
}

export default function OkrTargetFrequencySettings() {
  const { hasPermission } = useAuth();
  const canModify = hasPermission('okr_modify');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [globalSel, setGlobalSel] = useState([]);
  const [byEmployee, setByEmployee] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [pick, setPick] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/okr/target-frequency-settings');
      const d = r.data?.data || {};
      setGlobalSel(Array.isArray(d.global?.frequencies) ? [...d.global.frequencies] : []);
      setByEmployee(Array.isArray(d.by_employee) ? d.by_employee : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load OKR target frequency settings');
      setGlobalSel([]);
      setByEmployee([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/employees/directory-for-okr', { params: { limit: 500, page: 1 } });
        const rows = r.data?.data || [];
        setEmployeeOptions(
          rows.filter(
            (x) =>
              x.id &&
              !String(x.id).startsWith('pending:') &&
              x.status === 'active' &&
              !x.needsEmployeeProfile
          )
        );
      } catch {
        setEmployeeOptions([]);
      }
    })();
  }, []);

  const toggleGlobal = (key) => {
    setGlobalSel((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const saveGlobal = async () => {
    if (!canModify) return;
    setSaving(true);
    try {
      await api.put('/okr/target-frequency-settings/global', { frequencies: globalSel });
      toast.success('Organization default saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleCell = (employeeId, key) => {
    setByEmployee((rows) =>
      rows.map((row) => {
        if (String(row.employee_id) !== String(employeeId)) return row;
        const cur = Array.isArray(row.frequencies) ? row.frequencies : [];
        const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
        return { ...row, frequencies: next };
      })
    );
  };

  const saveEmployeeRow = async (employeeId) => {
    if (!canModify) return;
    const row = byEmployee.find((r) => String(r.employee_id) === String(employeeId));
    const frequencies = Array.isArray(row?.frequencies) ? row.frequencies : [];
    setSaving(true);
    try {
      await api.put(`/okr/target-frequency-settings/employees/${employeeId}`, { frequencies });
      toast.success('Employee row saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeEmployeeRow = async (employeeId) => {
    if (!canModify) return;
    if (!window.confirm('Remove this employee’s frequency override? They will follow the organization default.')) return;
    try {
      await api.delete(`/okr/target-frequency-settings/employees/${employeeId}`);
      toast.success('Removed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Remove failed');
    }
  };

  const addEmployeeToMatrix = async () => {
    if (!pick?.id || !canModify) {
      toast.error('Choose an employee from the list');
      return;
    }
    if (byEmployee.some((r) => String(r.employee_id) === String(pick.id))) {
      toast.error('That employee is already in the matrix');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/okr/target-frequency-settings/employees/${pick.id}`, { frequencies: [] });
      toast.success('Employee added to the matrix');
      setPick(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not add employee');
    } finally {
      setSaving(false);
    }
  };

  const comboLabel = (row) => {
    const c = row?.Contact;
    const name = c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '';
    return `${name || '—'} · ${row.employee_code || row.id}`;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        OKR Target Frequency Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose which reporting cadences apply for catalog OKR targets. Use <strong>All employees</strong> for the
        organization default (multi-select). Use <strong>Specific employees</strong> to override the default per person;
        saved rows are stored for later reference.
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          <Tab label="All employees (default)" />
          <Tab label="Specific employees (matrix)" />
        </Tabs>
      </Paper>

      {loading ? (
        <LinearProgress />
      ) : tab === 0 ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Organization default
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Multi-select is allowed (for example weekly and monthly together).
          </Typography>
          <FormGroup row sx={{ flexWrap: 'wrap', gap: 1 }}>
            {FREQ_OPTIONS.map((f) => (
              <FormControlLabel
                key={f.key}
                control={
                  <Checkbox
                    checked={globalSel.includes(f.key)}
                    onChange={() => toggleGlobal(f.key)}
                    disabled={!canModify || saving}
                  />
                }
                label={f.label}
              />
            ))}
          </FormGroup>
          <Divider sx={{ my: 2 }} />
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveGlobal}
            disabled={!canModify || saving}
          >
            {saving ? 'Saving…' : 'Save organization default'}
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Per-employee overrides
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Add an employee, tick one or more frequencies, then save that row. Empty selection is allowed if you want to
            clear reporting expectations for that person.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mb: 2 }}>
            <Autocomplete
              sx={{ minWidth: 280, flex: 1 }}
              options={employeeOptions}
              getOptionLabel={(o) => comboLabel(o)}
              value={pick}
              onChange={(_, v) => setPick(v)}
              disabled={!canModify || saving}
              renderInput={(params) => <TextField {...params} label="Employee" placeholder="Search" />}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
            />
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={addEmployeeToMatrix}
              disabled={!canModify || saving || !pick}
            >
              Add to matrix
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                {FREQ_OPTIONS.map((f) => (
                  <TableCell key={f.key} align="center" sx={{ fontWeight: 700 }}>
                    {f.label}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byEmployee.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No per-employee rows yet. Add employees above.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                byEmployee.map((row) => {
                  const freq = Array.isArray(row.frequencies) ? row.frequencies : [];
                  return (
                    <TableRow key={row.id || row.employee_id} hover>
                      <TableCell>{employeeLabel(row)}</TableCell>
                      {FREQ_OPTIONS.map((f) => (
                        <TableCell key={f.key} align="center">
                          <Checkbox
                            size="small"
                            checked={freq.includes(f.key)}
                            onChange={() => toggleCell(row.employee_id, f.key)}
                            disabled={!canModify || saving}
                          />
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<SaveIcon />}
                          onClick={() => saveEmployeeRow(row.employee_id)}
                          disabled={!canModify || saving}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => removeEmployeeRow(row.employee_id)}
                          disabled={!canModify || saving}
                          sx={{ ml: 0.5 }}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
